import type {
  RuleSet,
  StoredRuleSet,
  TreeNode,
  StoredTreeNode,
  QuestionNode,
  StoredQuestionNode,
  ResultNode,
  StoredResultNode,
} from "./types";
import type { Resolver, ResolveContext } from "../i18n/types";

function hydrateQuestion(
  node: StoredQuestionNode | QuestionNode,
  resolve: Resolver,
  ctx: ResolveContext
): QuestionNode {
  // "textKey" is required only on the stored shape, so it discriminates the union.
  const isStored = "textKey" in node;
  const text = isStored ? resolve(node.textKey, ctx) : node.text;
  const helpText = isStored
    ? node.helpKey !== undefined
      ? resolve(node.helpKey, ctx)
      : undefined
    : node.helpText;
  const options = isStored
    ? node.options?.map((o) => ({ value: o.value, label: resolve(o.labelKey, ctx), score: o.score }))
    : node.options;
  return {
    type: "question",
    text,
    helpText,
    inputType: node.inputType,
    category: node.category,
    options,
    next: node.next,
    audiences: node.audiences,
    skipTo: node.skipTo,
    setsComputed: node.setsComputed,
    allowAllApply: node.allowAllApply,
  };
}

function hydrateResult(
  node: StoredResultNode | ResultNode,
  resolve: Resolver,
  ctx: ResolveContext
): ResultNode {
  const isStored = "titleKey" in node;
  const recommendations = isStored
    ? node.recommendationKeys.map((k) => resolve(k, ctx))
    : node.recommendations;
  return {
    type: "result",
    determination: node.determination,
    score: node.score,
    title: isStored ? resolve(node.titleKey, ctx) : node.title,
    explanation: isStored ? resolve(node.explanationKey, ctx) : node.explanation,
    recommendations,
    audiences: node.audiences,
    skipTo: node.skipTo,
  };
}

export function hydrateRuleSet(
  rules: StoredRuleSet | RuleSet,
  resolve: Resolver,
  context: ResolveContext
): RuleSet {
  const nodes: Record<string, TreeNode> = {};
  for (const [id, node] of Object.entries(rules.nodes as Record<string, StoredTreeNode | TreeNode>)) {
    if (node.type === "question") nodes[id] = hydrateQuestion(node, resolve, context);
    else if (node.type === "result") nodes[id] = hydrateResult(node, resolve, context);
    else nodes[id] = node; // gate: structural only, no strings to resolve
  }
  return {
    id: rules.id,
    version: rules.version,
    metadata: rules.metadata,
    computedValues: rules.computedValues,
    root: rules.root,
    nodes,
  };
}
